import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StoryCard = ({ story, onViewDetails }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="bg-card rounded-lg construction-shadow overflow-hidden transition-construction hover:construction-shadow-lg group">
      {/* Hero Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={story.heroImage}
          alt={story.title}
          className={`w-full h-full object-cover transition-construction group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Project Type Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
            {story.projectType}
          </span>
        </div>

        {/* Key Metrics */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={16} />
                <span className="text-sm font-medium">{story.timeReduction}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="TrendingDown" size={16} />
                <span className="text-sm font-medium">₹{story.costSavings}</span>
              </div>
            </div>
            {story.hasVideo && (
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="Play" size={16} className="text-white ml-0.5" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-headline font-bold text-lg text-card-foreground group-hover:text-primary transition-construction">
            {story.title}
          </h3>
          <div className="flex items-center space-x-1 text-warning">
            {[...Array(5)].map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={14}
                className={i < story.rating ? 'fill-current' : 'opacity-30'}
              />
            ))}
          </div>
        </div>

        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {story.description}
        </p>

        {/* Client Info */}
        <div className="flex items-center space-x-3 mb-4">
          <Image
            src={story.client.avatar}
            alt={story.client.name}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <p className="font-medium text-sm text-card-foreground">{story.client.name}</p>
            <p className="text-xs text-muted-foreground">{story.client.location}</p>
          </div>
        </div>

        {/* Key Results */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-lg font-bold text-success">{story.results.timeline}</p>
            <p className="text-xs text-muted-foreground">Timeline</p>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-lg font-bold text-primary">₹{story.results.budget}</p>
            <p className="text-xs text-muted-foreground">Budget</p>
          </div>
        </div>

        {/* Services Used */}
        <div className="flex flex-wrap gap-2 mb-4">
          {story.servicesUsed.slice(0, 3).map((service, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full"
            >
              {service}
            </span>
          ))}
          {story.servicesUsed.length > 3 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
              +{story.servicesUsed.length - 3} more
            </span>
          )}
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => onViewDetails(story)}
          className="group-hover:bg-primary group-hover:text-primary-foreground transition-construction"
        >
          View Full Story
        </Button>
      </div>
    </div>
  );
};

export default StoryCard;